// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/shared/dialogs/cal-error-dialog/cal-error-dialog.component.ts
// VERSION: v1.0.0 - Client Access License Error Dialog
// DATE: 2025-01-09
//
// PURPOSE: Beautiful Material Dialog for CAL (Client Access License) issues
// AUTO-REDIRECTS: After 5 seconds to login/home page
// HANDLES: no_license, expired, suspended states
// ═══════════════════════════════════════════════════════════════════════════════

import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

/**
 * Dialog Data Interface
 */
export interface CalErrorDialogData {
  status: 'active' | 'suspended' | 'expired' | 'no_license';
  hasLicense: boolean;
  daysRemaining: number | null;
  expiresAt: number | null;
  assignedBy: string | null;
  assignedAt: string | null;
  redirectDelay?: number; // Delay in seconds (default: 5)
}

@Component({
  selector: 'app-cal-error-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './cal-error-dialog.component.html',
  styleUrls: ['./cal-error-dialog.component.scss']
})
export class CalErrorDialogComponent implements OnInit, OnDestroy {
  
  countdown: number;
  private countdownInterval: any;
  redirectUrl: string;
  
  constructor(
    public dialogRef: MatDialogRef<CalErrorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CalErrorDialogData,
    private router: Router
  ) {
    // Disable closing via backdrop or ESC key
    this.dialogRef.disableClose = true;
    
    // Set countdown (default: 9 seconds - 5 were too fast, no chance to read)
    this.countdown = data.redirectDelay || 9;
    
    // Construct redirect URL
    this.redirectUrl = this.constructRedirectUrl();
    
    console.log('[CalErrorDialog] Opened with:', {
      status: data.status,
      hasLicense: data.hasLicense,
      countdown: this.countdown,
      redirectUrl: this.redirectUrl
    });
  }
  
  ngOnInit(): void {
    this.startCountdown();
  }
  
  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
  
  /**
   * Construct redirect URL based on current hostname
   * Always redirects to root (login page)
   */
  private constructRedirectUrl(): string {
    const hostname = window.location.hostname;
    
    if (hostname.includes('localhost')) {
      return 'http://localhost:4200/';
    }
    
    return `https://${hostname}/`;
  }
  
  /**
   * Start countdown timer and redirect when done
   */
  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      
      if (this.countdown <= 0) {
        this.redirectToHome();
      }
    }, 1000);
  }
  
  /**
   * Redirect to home/login page
   */
  private redirectToHome(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    console.log('[CalErrorDialog] Redirecting to home:', this.redirectUrl);
    
    // Close dialog first
    this.dialogRef.close();
    
    // Use window.location for full page reload (clears all state)
    window.location.href = this.redirectUrl;
  }
  
  /**
   * User clicked "Go to Home Now" button
   * Redirects immediately without waiting
   */
  onGoHomeNow(): void {
    console.log('[CalErrorDialog] User clicked: GO HOME NOW');
    this.redirectToHome();
  }
  
  /**
   * Contact Admin button click
   * Optional: Can implement email/ticket system
   */
  onContactAdmin(): void {
    console.log('[CalErrorDialog] User clicked: CONTACT ADMIN');
    
    // Optional: Open email client
    if (this.data.assignedBy) {
      const subject = encodeURIComponent('CEEQ.IT License Issue');
      const body = encodeURIComponent(
        `Hello,\n\nI'm experiencing issues with my CEEQ.IT Client Access License.\n\n` +
        `Status: ${this.data.status}\n` +
        `License ID: ${this.data.hasLicense ? 'Assigned' : 'Not Assigned'}\n\n` +
        `Please help me resolve this issue.\n\nThank you!`
      );
      
      window.open(`mailto:${this.data.assignedBy}?subject=${subject}&body=${body}`);
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // DISPLAY HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Get error title based on CAL status
   */
  getErrorTitle(): string {
    switch (this.data.status) {
      case 'no_license':
        return 'No License Assigned';
      case 'expired':
        return 'License Expired';
      case 'suspended':
        return 'License Suspended';
      default:
        return 'License Issue';
    }
  }
  
  /**
   * Get user-friendly error description
   */
  getErrorDescription(): string {
    switch (this.data.status) {
      case 'no_license':
        return 'You don\'t have a Client Access License assigned to your account. Please contact your administrator to request access.';
      
      case 'expired':
        if (this.data.expiresAt) {
          const expiredDate = new Date(this.data.expiresAt * 1000);
          return `Your license expired on ${expiredDate.toLocaleDateString()}. Please contact your administrator to renew your access.`;
        }
        return 'Your Client Access License has expired. Please contact your administrator to renew your access.';
      
      case 'suspended':
        return 'Your Client Access License has been suspended. Please contact your administrator for more information.';
      
      default:
        return 'There is an issue with your Client Access License. Please contact your administrator.';
    }
  }
  
  /**
   * Get icon name based on status
   */
  getIconName(): string {
    switch (this.data.status) {
      case 'no_license':
        return 'badge';
      case 'expired':
        return 'schedule';
      case 'suspended':
        return 'block';
      default:
        return 'warning';
    }
  }
  
  /**
   * Get formatted expiration date
   */
  getFormattedExpirationDate(): string {
    if (!this.data.expiresAt) return 'N/A';
    
    const date = new Date(this.data.expiresAt * 1000);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  /**
   * Get formatted assignment date
   */
  getFormattedAssignmentDate(): string {
    if (!this.data.assignedAt) return 'N/A';
    
    const date = new Date(this.data.assignedAt);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  /**
   * Check if admin contact is available
   */
  hasAdminContact(): boolean {
    return !!this.data.assignedBy;
  }
}

/* END OF FILE */
